/**
 * Token Refresh Wrapper for API Client
 * Handles automatic token refresh on 401 errors
 */

import { store } from '../redux/configureStore';
import {
  refreshTokenBegin,
  refreshTokenSuccess,
  refreshTokenError,
  logoutUser,
} from '../redux/actions/accountActions';
import { showError } from '../redux/actions/errorActions';
import { apiRequest, ApiRequestOptions } from './client';
import { BusinessError } from './types';

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

/**
 * Refresh the authentication token
 */
async function refreshToken(): Promise<void> {
  store.dispatch(refreshTokenBegin());

  try {
    const response = await apiRequest('/accounts/refresh-token', {
      method: 'POST',
      credentials: 'include',
      skipAuth: true, // Don't add Authorization header for refresh
    });

    store.dispatch(refreshTokenSuccess(response));
    
    // Update localStorage
    const state = store.getState();
    localStorage.setItem('account', JSON.stringify(state.account.account));
  } catch (error: any) {
    store.dispatch(refreshTokenError(error.message || 'Token refresh failed'));

    // Show error dialog for specific cases
    if (error instanceof BusinessError) {
      if (error.status === 400 || error.status === 500) {
        store.dispatch(showError(error.message));
      }
    }

    throw error;
  }
}

/**
 * Check if token should be refreshed based on error
 */
function shouldRefreshToken(error: any): boolean {
  if (error instanceof BusinessError) {
    return error.status === 401;
  }
  return false;
}

/**
 * Make an API request with automatic token refresh on 401
 */
export async function apiRequestWithRefresh<T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  try {
    return await apiRequest<T>(endpoint, options);
  } catch (error) {
    // Check if we should refresh the token
    if (shouldRefreshToken(error)) {
      // If already refreshing, wait for that to complete
      if (isRefreshing && refreshPromise) {
        await refreshPromise;
      } else {
        // Start refresh
        isRefreshing = true;
        refreshPromise = refreshToken()
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });

        try {
          await refreshPromise;
        } catch (refreshError) {
          // Refresh failed, logout user
          store.dispatch(logoutUser());
          throw refreshError;
        }
      }

      // Retry the original request with new token
      return await apiRequest<T>(endpoint, options);
    }

    // Not a 401 error, re-throw
    throw error;
  }
}

/**
 * Convenience methods with token refresh
 */
export const apiWithRefresh = {
  get: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequestWithRefresh<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: ApiRequestOptions) =>
    apiRequestWithRefresh<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: ApiRequestOptions) =>
    apiRequestWithRefresh<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: ApiRequestOptions) =>
    apiRequestWithRefresh<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequestWithRefresh<T>(endpoint, { ...options, method: 'DELETE' }),
};

